// Function to extract accent color from various sources
const extractAccentColor = (doc, url) => {
  console.log('Extracting accent color from:', url);
  
  // Try to get color from theme-color meta tag
  const themeColor = doc.querySelector('meta[name="theme-color"]')?.getAttribute('content')?.trim();
  console.log('Theme color found:', themeColor);
  if (themeColor && isValidColor(themeColor)) {
    console.log('Using theme color:', themeColor);
    return themeColor;
  }
  
  // Try to get color from msapplication-TileColor
  const tileColor = doc.querySelector('meta[name="msapplication-TileColor"]')?.getAttribute('content')?.trim();
  if (tileColor && isValidColor(tileColor)) {
    return tileColor;
  }
  
  // Try to get color from CSS custom properties or inline styles
  const styleSheets = doc.querySelectorAll('style, link[rel="stylesheet"]');
  console.log('Found style sheets:', styleSheets.length);
  
  for (const styleSheet of styleSheets) {
    if (styleSheet.textContent) {
      const cssText = styleSheet.textContent;
      console.log('CSS text length:', cssText.length);
      
      // Look for specific CSS custom properties in order of preference
      const colorProperties = [
        '--ghost-accent-color',
        '--primary-color',
        '--accent-color', 
        '--brand-color',
        '--main-color',
        '--theme-color',
        '--color-primary',
        '--color-accent'
      ];
      
      for (const prop of colorProperties) {
        const regex = new RegExp(`${prop}\\s*:\\s*([^;]+)`, 'gi');
        const matches = cssText.match(regex);
        console.log(`Looking for ${prop}, found matches:`, matches);
        if (matches) {
          for (const match of matches) {
            const color = match.split(':')[1]?.trim();
            console.log(`Found color for ${prop}:`, color);
            if (color && isValidColor(color)) {
              console.log('Using CSS custom property color:', color);
              return color;
            }
          }
        }
      }
    }
  }
  
  // Try to get color from favicon or apple-touch-icon
  const favicon = doc.querySelector('link[rel="icon"]')?.getAttribute('href') ||
                  doc.querySelector('link[rel="shortcut icon"]')?.getAttribute('href');
  if (favicon) {
    // This would require image analysis, for now we'll skip
  }
  
  // Try to get color from inline styles on body or html elements
  const bodyStyle = doc.querySelector('body')?.getAttribute('style');
  if (bodyStyle) {
    const backgroundColor = bodyStyle.match(/background-color:\s*([^;]+)/i);
    if (backgroundColor && backgroundColor[1] && isValidColor(backgroundColor[1])) {
      return backgroundColor[1];
    }
  }
  
  // Try to get color from any element with inline styles
  const elementsWithStyle = doc.querySelectorAll('[style*="color"], [style*="background"]');
  for (const element of elementsWithStyle) {
    const style = element.getAttribute('style');
    if (style) {
      // Look for color or background-color in inline styles
      const colorMatch = style.match(/(?:color|background-color):\s*([^;]+)/i);
      if (colorMatch && colorMatch[1] && isValidColor(colorMatch[1])) {
        return colorMatch[1];
      }
    }
  }
  
  // Final fallback: search the entire document for CSS custom properties
  const allText = doc.documentElement.outerHTML;
  console.log('Searching entire document for CSS custom properties');
  const colorProperties = [
    '--ghost-accent-color',
    '--primary-color',
    '--accent-color', 
    '--brand-color',
    '--main-color',
    '--theme-color',
    '--color-primary',
    '--color-accent'
  ];
  
  for (const prop of colorProperties) {
    const regex = new RegExp(`${prop}\\s*:\\s*([^;\\s]+)`, 'gi');
    const matches = allText.match(regex);
    console.log(`Document-wide search for ${prop}, found matches:`, matches);
    if (matches) {
      for (const match of matches) {
        const color = match.split(':')[1]?.trim();
        console.log(`Found color in document for ${prop}:`, color);
        if (color && isValidColor(color)) {
          console.log('Using document-wide CSS custom property color:', color);
          return color;
        }
      }
    }
  }
  
  console.log('No accent color found, returning null');
  return null;
};

// Helper function to validate if a color is valid
const isValidColor = (color) => {
  if (!color) return false;
  
  // Remove any whitespace
  color = color.trim();
  
  // Check for hex colors
  if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
    return true;
  }
  
  // Check for rgb/rgba colors
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+)?\s*\)$/.test(color)) {
    return true;
  }
  
  // Check for hsl/hsla colors
  if (/^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*(,\s*[\d.]+)?\s*\)$/.test(color)) {
    return true;
  }
  
  // Check for named colors (basic set)
  const namedColors = [
    'red', 'green', 'blue', 'yellow', 'orange', 'purple', 'pink', 'brown', 'black', 'white',
    'gray', 'grey', 'navy', 'teal', 'lime', 'olive', 'maroon', 'aqua', 'fuchsia', 'silver'
  ];
  if (namedColors.includes(color.toLowerCase())) {
    return true;
  }
  
  return false;
};

// Utility function to fetch metadata from a URL
export const fetchMetadata = async (url) => {
  try {
    let html;
    
    try {
      // Try direct fetch first
      console.log('Attempting direct fetch for:', url);
      const response = await fetch(url, {
        mode: 'cors',
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
          'User-Agent': 'Mozilla/5.0 (compatible; MetadataFetcher/1.0)'
        }
      });
      
      if (!response.ok) {
        throw new Error('Direct fetch failed');
      }
      
      html = await response.text();
      console.log('Direct fetch successful');
    } catch (directError) {
      console.log('Direct fetch failed, trying Netlify function proxy:', directError.message);
      // Fallback to our Netlify function
      const proxyUrl = `/api/fetch-metadata?url=${encodeURIComponent(url)}`;
      const proxyResponse = await fetch(proxyUrl);
      
      if (!proxyResponse.ok) {
        throw new Error('Both direct fetch and Netlify function failed');
      }
      
      html = await proxyResponse.text();
      console.log('Netlify function fetch successful');
    }
    
    // Parse the HTML to extract metadata
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Extract title
    const title = doc.querySelector('title')?.textContent?.trim() || 
                  doc.querySelector('meta[property="og:title"]')?.getAttribute('content')?.trim() ||
                  doc.querySelector('meta[name="title"]')?.getAttribute('content')?.trim() || '';
    
    // Extract description
    const description = doc.querySelector('meta[property="og:description"]')?.getAttribute('content')?.trim() ||
                      doc.querySelector('meta[name="description"]')?.getAttribute('content')?.trim() ||
                      doc.querySelector('meta[name="twitter:description"]')?.getAttribute('content')?.trim() || '';
    
    // Extract image
    const image = doc.querySelector('meta[property="og:image"]')?.getAttribute('content')?.trim() ||
                 doc.querySelector('meta[name="twitter:image"]')?.getAttribute('content')?.trim() ||
                 doc.querySelector('meta[name="image"]')?.getAttribute('content')?.trim() || '';
    
    // Make image URL absolute if it's relative
    const absoluteImageUrl = image ? new URL(image, url).href : '';
    
    // Extract accent color from various sources
    const accentColor = extractAccentColor(doc, url);
    console.log('Extracted accent color:', accentColor);
    
    return {
      title: title || '',
      description: description || '',
      image: absoluteImageUrl || '',
      accentColor: accentColor || '#667eea' // fallback to blue
    };
  } catch (error) {
    console.error('Error fetching metadata:', error);
    return {
      title: '',
      description: '',
      image: '',
      accentColor: '#667eea' // fallback to blue
    };
  }
};
